// @flow

import { disbatch } from 'redux-act';

import { addCartProducts, getCart } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import type { AvailabilityIconName } from 'src/Domain/Icon/IconContainer';
import {
  getResultProductData,
  startAddingToCart,
  stopAddingToCart,
} from 'src/DomainTransfer/modules/actions';
import { useAppSelector } from 'src/hooks';
import type { Dispatch, GetState } from 'src/types';

import { selectResults } from '../../modules/selectors/results';
import type { ResultItem } from '../../types';

export type DomainTransferPossibility =
  | 'transferable'
  | 'creatable'
  | 'error'
  | 'none'
  | 'untransferable'
  | 'loading';

export const getDomainPossibility = (
  domain: ResultItem,
): DomainTransferPossibility => {
  const { isTransferable, invalid, isWorking, availability } = domain;

  if (isTransferable) return 'transferable';

  if (!isTransferable && !isWorking && !invalid && availability === 'available')
    return 'creatable';

  if (!isTransferable && !isWorking && !invalid) return 'untransferable';

  if (!isTransferable && !isWorking && invalid) return 'none';

  return 'loading';
};

export const transferPossibility2AvailabilityIconName: Record<
  DomainTransferPossibility,
  AvailabilityIconName,
> = {
  transferable: 'ok',
  creatable: 'ok',
  none: 'fail',
  untransferable: 'fail',
  loading: 'warning',
  error: 'fail',
};

export const useAvailabilityText = ({
  domain,
}: {
  domain: ResultItem,
}): string => {
  const { __ } = useTranslator();

  const possibility = getDomainPossibility(domain);

  if (possibility === 'transferable')
    return __('This domain can be transferred to Gandi.');

  if (possibility === 'untransferable')
    return __('This domain cannot be transferred to Gandi. ');

  if (possibility === 'creatable')
    return __('This domain is available for registration!');

  if (possibility === 'none') return __('This is not a valid domain name.');
  if (possibility === 'error')
    return __('Sorry, something unexpected happened. Retry later.');

  // it should be a loading state
  return '';
};

export const useChangeText = ({
  change,
  duration,
}: {
  change: string,
  duration: number,
}): string => {
  const { __ } = useTranslator();

  if (change === 'unchanged') {
    return __("Expiration date won't change");
  }

  if (change === 'renew') {
    return __('Will be renewed for %(count)s years', { count: duration });
  }

  if (change === 'reset') {
    return __('Will expire in %(count)s years', { count: duration });
  }

  return '';
};

type SortedResults = Record<DomainTransferPossibility, Array<ResultItem>>;

export const useSortResultsByPossibility = (): SortedResults => {
  const results: SortedResults = {
    transferable: [],
    creatable: [],
    loading: [],
    untransferable: [],
    none: [],
    error: [],
  };

  const allResults = useAppSelector(selectResults);

  allResults.forEach((result: ResultItem) => {
    const possibility = getDomainPossibility(result);
    results[possibility].push(result);
  });

  return results;
};

export const addAllToCart = async (
  dispatch: Dispatch,
  getState: GetState,
): Promise<any> => {
  const results = selectResults(getState()).filter(
    result => !result.addedToCart && result.isTransferable,
  );

  const products = results.reduce((acc, result) => {
    const { productData, extra } = getResultProductData(result);

    return {
      // $FlowIgnore[prop-missing]
      data: acc.data ? [...acc.data, productData] : [productData],
      extra: {
        // $FlowIgnore[prop-missing]
        ...acc.extra,
        // $FlowFixMe[invalid-computed-prop]
        [productData.payload?.fqdn]: extra,
      },
    };
  }, {});

  try {
    disbatch(
      dispatch,
      results.map(({ fqdn }) => startAddingToCart(fqdn)),
    );
    await dispatch(
      addCartProducts({
        // $FlowFixMe[incompatible-call]
        productsData: products.data,
        extra: products.extra,
        options: {
          headers: { 'X-Dry-Run': 0 },
        },
      }),
    );
  } finally {
    disbatch(
      dispatch,
      results.map(({ fqdn }) => stopAddingToCart(fqdn)),
    );
  }

  return dispatch(getCart());
};
